<template>
  <div class="container py-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-1">Bereich-Information</h2>
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
                Bereiche
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-detail', params: { id: $route.params.id } }" class="text-decoration-none">
                {{ selectedTeam?.name }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-settings', params: { id: $route.params.id } }" class="text-decoration-none">
                Bereich-Einstellungen
              </router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">Bereich-Information</li>
          </ol>
        </nav>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" @click="goBack">
          <font-awesome-icon icon="arrow-left" /> Zurück
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- Content -->
    <div v-else-if="selectedTeam">
      <div class="row">
        <div class="col-md-8">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <font-awesome-icon icon="info-circle" class="me-2" />
                Bereich Information auf Quittung
              </h5>
            </div>
            <div class="card-body">
              <form @submit.prevent="saveTeamInfo">
                <div class="mb-3">
                  <label for="receiptFooterTeamName" class="form-label fw-semibold">
                    Bereich-Name
                    <small class="text-muted">(optional)</small>
                  </label>
                  <input
                    id="receiptFooterTeamName"
                    class="form-control"
                    v-model="receiptFooterTeamName"
                    placeholder="Name auf der Quittung (z.B. Vereinsname)"
                  />
                </div>
                <div class="mb-3">
                  <label for="receiptFooterText" class="form-label fw-semibold">
                    Zusätzliche Informationen
                    <small class="text-muted">(optional)</small>
                  </label>
                  <textarea 
                    id="receiptFooterText"
                    class="form-control" 
                    rows="6" 
                    v-model="receiptFooterText"
                    placeholder="Zusätzliche Informationen, z.B.  Adresse, Kontaktdaten..."
                  ></textarea>
                  <div class="form-text">
                    Dieser Text wird unterhalb des Bereich-Namen auf der PDF angezeigt.
                  </div>
                </div>
                <div class="d-flex justify-content-end">
                  <button type="submit" class="btn btn-primary" :disabled="savingTeamInfo">
                    <font-awesome-icon 
                      :icon="savingTeamInfo ? 'spinner' : 'save'" 
                      :class="{ 'fa-spin': savingTeamInfo }" 
                      class="me-1" 
                    />
                    {{ savingTeamInfo ? 'Speichern...' : 'Bereich-Info speichern' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <font-awesome-icon icon="info-circle" class="me-2" />
                Hilfe
              </h6>
            </div>
            <div class="card-body">
              <p class="small text-muted">
                Hier kannst du Informationen konfigurieren, die auf deinen PDF's angezeigt werden.
              </p>
              <ul class="small text-muted">
                <li>Der Bereich-Name wird auf der PDF angezeigt</li>
                <li>Zusätzliche Informationen können Adresse, Kontaktdaten oder Hinweise enthalten</li>
                <li>Diese Einstellungen gelten für alle Quittungen des Teams</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bereich not found -->
    <div v-else class="alert alert-gradient-warning" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      Bereich nicht gefunden.
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import { showSuccess, showError } from '@/services/notification'

const router = useRouter()
const route = useRoute()
const teamsStore = useTeamsStore()

const { selectedTeam, loading, error } = storeToRefs(teamsStore)

const receiptFooterText = ref('')
const receiptFooterTeamName = ref('')
const savingTeamInfo = ref(false)


watch(() => selectedTeam.value, (newTeam) => {
  if (newTeam) {
    receiptFooterText.value = newTeam.receiptFooterText || ''
    receiptFooterTeamName.value = newTeam.receiptFooterTeamName || ''
  }
}, { immediate: true })

const goBack = () => {
  router.push({ name: 'teams-settings', params: { id: route.params.id } })
}

const saveTeamInfo = async () => {
  if (!selectedTeam.value) return

  savingTeamInfo.value = true
  try {
    const settings = {
      receiptFooterTeamName: receiptFooterTeamName.value.trim() || '',
      receiptFooterText: receiptFooterText.value.trim() || ''
    }
    await teamsStore.updateTeamReceiptSettings(selectedTeam.value.id, settings)
    

    await teamsStore.loadTeamById(selectedTeam.value.id)
    
    showSuccess('Bereich-Information erfolgreich gespeichert!')
  } catch (error) {
    console.error('Fehler beim Speichern der Bereich-Information:', error)

    const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Fehler beim Speichern der Einstellungen. Bitte versuch es erneut.'
    showError(errorMessage)
  } finally {
    savingTeamInfo.value = false
  }
}

onMounted(async () => {

  if (!selectedTeam.value || selectedTeam.value.id !== route.params.id) {
    await teamsStore.loadTeamById(route.params.id)
  }
})
</script>
